import React from "react";
import { Text as RNText, TextStyle, StyleProp } from "react-native";
import { fonts, fontSizes } from "~/styles/tokens";
import { useTheme } from "~/styles/theme";

type Variant =
  "hero" | "heading" | "subheading" | "title" | "body" | "caption" | "label";
type Align = "left" | "center" | "right";

interface TextProps {
  children: React.ReactNode;
  variant?: Variant;
  color?: string;
  align?: Align;
  style?: StyleProp<TextStyle>;
  numberOfLines?: number;
}

/** Font family + size per variant (theme-invariant). Colour is applied from
 *  the theme at render time. */
const variantShape: Record<Variant, TextStyle> = {
  hero: {
    fontFamily: fonts.display,
    fontSize: fontSizes.hero,
    lineHeight: fontSizes.hero * 1.16,
  },
  heading: {
    fontFamily: fonts.display,
    fontSize: fontSizes.xxl,
    lineHeight: fontSizes.xxl * 1.2,
  },
  subheading: {
    fontFamily: fonts.subheading,
    fontSize: fontSizes.xl,
    lineHeight: fontSizes.xl * 1.25,
    letterSpacing: -0.2,
  },
  title: {
    fontFamily: fonts.bodySemibold,
    fontSize: fontSizes.lg,
    lineHeight: fontSizes.lg * 1.3,
  },
  body: {
    fontFamily: fonts.body,
    fontSize: fontSizes.md,
    lineHeight: fontSizes.md * 1.5,
  },
  caption: {
    fontFamily: fonts.body,
    fontSize: fontSizes.sm,
    lineHeight: fontSizes.sm * 1.45,
  },
  label: {
    fontFamily: fonts.bodyMedium,
    fontSize: fontSizes.xs,
    lineHeight: fontSizes.xs * 1.4,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
};

export const Text: React.FC<TextProps> = ({
  children,
  variant = "body",
  color,
  align = "left",
  style,
  numberOfLines,
}) => {
  const { colors } = useTheme();
  const defaultColor =
    variant === "caption" || variant === "label"
      ? colors.muted
      : colors.foreground;

  return (
    <RNText
      style={[
        variantShape[variant],
        { color: color ?? defaultColor, textAlign: align },
        style,
      ]}
      numberOfLines={numberOfLines}
    >
      {children}
    </RNText>
  );
};
